import React, { useState, ReactNode } from 'react';

interface TabProps {
  label: string;
  children: ReactNode;
  keepMounted?: boolean; // si true, le contenu reste monté mais masqué lors du changement d'onglet
  /**
   * Contenu affiché à l'extrême droite de la barre d'onglets, au même niveau que
   * les onglets, quand CELUI-CI est actif : rappel du contexte de travail (nom de
   * la chaîne, de l'utilisateur…).
   *
   * Porté par l'onglet et non par un index côté appelant : réordonner ou insérer
   * un onglet ne demande alors aucune mise à jour, et un onglet qui ne veut rien
   * afficher se contente d'omettre la propriété.
   */
  headerRight?: ReactNode;
  /**
   * Onglet visible mais inaccessible (ex. chaîne éphémère) : grisé, curseur "interdit",
   * clic sans effet — `disabledMessage` s'affiche au survol (title natif du navigateur).
   */
  disabled?: boolean;
  disabledMessage?: string;
}

interface Tabs2Props {
  tabs: TabProps[];
  onChange?: (index: number) => void;  // ← nouveau
  initialTabIndex?: number;
  /**
   * Onglet actif imposé par l'appelant, quand il doit pouvoir changer d'onglet lui-même
   * (ex. « Actions rapides » du tableau de bord d'une chaîne). Omis : l'onglet actif reste
   * un état interne, seul le clic le change.
   */
  activeTabIndex?: number;
}

const Tabs2: React.FC<Tabs2Props> = ({ tabs, onChange, initialTabIndex = 0, activeTabIndex: controlledIndex }) => {

  const [internalIndex, setActiveTabIndex] = useState(initialTabIndex);
  const activeTabIndex = controlledIndex ?? internalIndex;

  const handleTabClick = (index: number) => {
    if (tabs[index]?.disabled) return;
    setActiveTabIndex(index);
	onChange?.(index);
  };

  return (
    <div style={{ height: 'fit-content', display: 'flex', flexDirection: 'column', gap: '0px' }}>

      {/* Barre d'onglets */}
      <div style={{ display: 'flex', alignItems: 'stretch', cursor: 'pointer', height: 'fit-content' }}>
        {tabs.map((tab, index) => (
          <div
            key     = {index}
            onClick = {() => handleTabClick(index)}
            title   = {tab.disabled ? tab.disabledMessage : undefined}
            style   = {{
              padding      : '10px 20px',
              borderBottom : activeTabIndex === index ? '2px solid var(--color-Tab-Index-Active-BorderBottom)' : 'none',
              color        : activeTabIndex === index ? 'var(--color-Tab-Index-Active-FG)' : 'var(--color-Tab-Index-Inactive-FG)',
              marginBottom : '5px',
              opacity      : tab.disabled ? 0.4 : 1,
              cursor       : tab.disabled ? 'not-allowed' : 'pointer',
            }}
          >
            {tab.label}
          </div>
        ))}

        {/* Rappel de contexte de l'onglet actif, poussé à droite par la marge auto.
            `cursor: default` parce que la barre entière est en `pointer` : sans ça,
            ce texte non cliquable inviterait au clic. */}
        {tabs[activeTabIndex]?.headerRight && (
          <div
            style={{
              marginLeft   : 'auto',
              display      : 'flex',
              alignItems   : 'center',
              padding      : '10px 20px',
              marginBottom : '5px',
              cursor       : 'default',
              minWidth     : 0,
            }}
          >
            {tabs[activeTabIndex].headerRight}
          </div>
        )}
      </div>

      {/* Contenu des onglets */}
      <div>
        {tabs.map((tab, index) => {
          const isActive = activeTabIndex === index;

          // Onglet sans keepMounted : comportement original (monte/démonte)
          if (!tab.keepMounted) {
            return isActive ? <div key={index}>{tab.children}</div> : null;
          }

          // Onglet avec keepMounted : toujours monté, masqué via display:none
          return (
            <div key={index} style={{ display: isActive ? undefined : 'none' }}>
              {tab.children}
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default Tabs2;